// Modules
import { useCallback, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useHistory, useLocation } from 'react-router-dom'

// Application
import { tracking } from 'constants/tracking'
import { useLoginService } from 'services/useLoginService'
import { clearCpf, convertToNumber } from 'utils'
import { ROUTES_PATH } from 'constants/routesPath'
import { useLoginContext, useSignUp } from 'context'
import { useAccountMDMHook, useLoginHook, useRedirectTo } from 'hooks'
import { defaultConfig } from 'modules/RemoteConfig/defaultConfig'
import { useRemoteConfig } from 'modules/RemoteConfig/hooks/useRemoteConfig'
import { useAccountService } from 'services/account'
import { AuthState, LoginType, SignInCredentials, TokenPayload, handleAuthUserAfterChangePasswordProps } from 'typings/auth'
import { usePhasedLogin } from '../usePhasedLogin'
import { useSignUpStepper } from '../useSignUpStepper'
import { useSignUpAnaytics } from 'hooks/useSignUpAnaytics'
import { useIsLoginStore, useLogInfoStore } from 'redesign/store'
import { UNICO_CONSTANTS } from 'redesign/constants'

export const useAuthentication = () => {
    const history = useHistory()
    const { getConfigByName } = useRemoteConfig()
    const service = useLoginService()
    const location = useLocation()
    const { operations } = useLoginContext()
    const { executeRecaptcha = () => '' } = useGoogleReCaptcha()
    const { setPersonalData, verifyPreUserRegistration } = useSignUp()
    const { setStepSize } = useSignUpStepper()
    const { nextStep, onboardingPage, setUrlNextOnboardingStep, dispatchAnalytics } = usePhasedLogin()
    const { checkAccountService } = useAccountService()
    const { setPersonalData: setPersonalDataMDM } = useAccountMDMHook()
    const { verifyUser } = useLoginHook()
    const [hasValidatedContact, setHasValidatedContact] = useState(false)
    const [failedPasswordCount, setFailedPasswordCount] = useState(0)
    const { redirectToPath, newRedirectFlow } = useRedirectTo()
    const { dispatchAnalyticsEventFeedBack } = useSignUpAnaytics()
    const { logInfo } = useLogInfoStore()
    const { isLogin, setIsLogin } = useIsLoginStore()

    let redirectLink = `${ROUTES_PATH.FORWARDING_APP}${location.search}`

    if (!newRedirectFlow) {
        redirectLink = (getConfigByName('AUTH_REDIRECT_LOGIN') as string) || defaultConfig.strings.AUTH_REDIRECT_LOGIN
    }

    const checkRedesignLocalFlag = useCallback(
        async (value: boolean) => {
            const storage = localStorage.getItem('@Livia') as string
            const typedStorage = JSON.parse(storage) as TokenPayload
            typedStorage.redesign = value
            operations.addPayloadTokenLS({
                ...typedStorage,
            })
        },
        [operations],
    )

    const authenticate = async (credentials: SignInCredentials): Promise<void> => {
        const credentialsWithLogInfo: SignInCredentials = {
            ...credentials,
            logInfo,
        }
        const data = await service.authenticate(credentialsWithLogInfo)

        const payload: TokenPayload = {
            token: data?.accessToken,
            refreshToken: data?.refreshToken,
            limitedFlow: false,
            redesign: undefined,
        }

        if (data?.uuid) {
            payload.keepLogged = {
                keepLogged: credentials.keepLogged?.keepLogged,
                latitude: credentials.keepLogged?.latitude,
                longitude: credentials.keepLogged?.longitude,
                address: credentials.keepLogged?.address,
                uuid: data.uuid,
                patientId: data.userId,
                username: credentials.username,
            }
            operations.addPayloadKeepLoggedLS({
                token: data?.accessToken,
                refreshToken: data?.refreshToken,
                keepLogged: {
                    ...payload.keepLogged,
                },
            })
        }

        operations.addPayloadTokenLS(payload)
        operations.setIdentifier(LoginType.empty)
    }

    const authenticateWithLink = async ({ cpf, id }: { cpf: string; id: string }): Promise<void> => {
        const credentialsWithLogInfo = {
            federalTaxId: cpf,
            magicLinkId: id,
        }

        const data = await service.authenticateWithLink(credentialsWithLogInfo)

        const payload = {
            token: data?.accessToken,
            refreshToken: data?.refreshToken,
        }

        operations.addPayloadTokenLS(payload)
        operations.setIdentifier(LoginType.cpf)
    }

    const logout = async (): Promise<void> => {
        const AuthStateLS = JSON.parse(localStorage.getItem('@Livia')!) as AuthState

        if (AuthStateLS?.keepLogged?.uuid) {
            await service.logoutUser(AuthStateLS.keepLogged.uuid)
        }
    }

    const checkMdmAccount = async (cpf: string) => {
        try {
            const { biologicalSex, phoneContacts, emailContacts } = await verifyPreUserRegistration({ cpf, birthDate: '', name: '' })

            const personalData = {
                cpf,
                biologicalSex,
                phoneContacts,
                emailContacts,
            }

            await setPersonalDataMDM(personalData)

            if (!biologicalSex) {
                setStepSize(5)
                return ROUTES_PATH.SIGN_UP_PERSONAL_DATA
            }

            setStepSize(3)
            return ROUTES_PATH.SIGN_UP_MDM_CREATE_PASSWORD
        } catch (e) {
            setStepSize(5)
            return ROUTES_PATH.SIGN_UP_PERSONAL_DATA
        }
    }

    const resetFailedPasswordCount = () => setFailedPasswordCount(0)

    const handleAuthUser = async ({
        cpf,
        password,
        keepLogged = false,
        latitude = null,
        longitude = null,
        address = null,
        setTouched,
        setFieldError,
        setSubmitting,
        setFieldValue,
    }: {
        cpf: string
        password: string
        keepLogged?: boolean
        latitude?: string | null
        longitude?: string | null
        address?: string | null
        setFieldError: (field: string, value: string | undefined) => void
        setTouched: (value: {}) => void
        setFieldValue: (field: string, value: boolean) => void
        setSubmitting: (value: boolean) => void
    }) => {
        try {
            const { status, accountLoginSearch, federalTaxId, hasCipPassword } = await checkAccountService(cpf)

            const biometyDataLS = { hasCipPassword }

            sessionStorage.setItem('@Livia/federalTaxId', federalTaxId)
            sessionStorage.setItem(UNICO_CONSTANTS.STORAGE, JSON.stringify(biometyDataLS))

            if (['LOGINNOTFOUND', 'ACCOUNTNOTFOUND', 'BACKOFFICE'].includes(status || '')) {
                setIsLogin(false)
                let { SIGN_UP_PERSONAL_DATA: RedirectRoute } = ROUTES_PATH
                const MDM_PATIENT = status === 'LOGINNOTFOUND' || status === 'BACKOFFICE'
                if (MDM_PATIENT) {
                    RedirectRoute = await checkMdmAccount(cpf)
                }

                setPersonalData({ cpf, accountExists: MDM_PATIENT })
                if (onboardingPage?.visibility) {
                    setUrlNextOnboardingStep(RedirectRoute)
                    nextStep()
                    return
                }
                return history.push(RedirectRoute)
            }

            const hasEmailOrMobileValidated = !!accountLoginSearch.email || !!accountLoginSearch.mobile
            setHasValidatedContact(hasEmailOrMobileValidated)

            if (!password) {
                setTouched({})
                return setFieldValue('showPasswordField', true)
            }

            const recaptchaToken = await executeRecaptcha('login')
            const payload = {
                username: convertToNumber(cpf),
                password,
                recaptchaToken: recaptchaToken,
                uniqueAccess: true,
                keepLogged: {
                    keepLogged,
                    latitude,
                    longitude,
                    address,
                },
            }

            await authenticate(payload)

            resetFailedPasswordCount()

            checkRedesignLocalFlag(true)
            const { isRedirect } = await verifyUser({
                externalStep: 'PHASED_LOGIN',
                loginType: tracking.LOGIN_TYPE.CPF,
                rememberMe: keepLogged,
            })

            if (!isRedirect) {
                redirectToPath({
                    defaultRedirect: redirectLink,
                    defaultRedirectActions: () => {
                        sessionStorage.removeItem('EXTERNAL_COMPANY_DATA')
                    },
                })
            }
        } catch (error) {
            const codeError = error?.graphQLErrors?.[0].extensions?.code

            if (codeError === 'NOTEXISTS' || codeError === 'DECEASED') {
                dispatchAnalyticsEventFeedBack('confirmCpf', { errorMessage: 'NOTEXISTS' })
                setFieldError('cpf', 'CPF inválido. Tente novamente ou tente acessar por Protocolo (CIP)')
                return
            }

            if (codeError === 'NOTACCEPTABLE') {
                setPersonalData({ cpf })
                const SIGN_UP_MDM_ROUTE = await checkMdmAccount(cpf)
                return history.push(SIGN_UP_MDM_ROUTE)
            }

            if (codeError === 'UNDERAGE') {
                dispatchAnalyticsEventFeedBack('confirmCpf', { errorMessage: 'UNDERAGE' })
                setFieldError(
                    'cpf',
                    'Entre com o CPF do responsável e adicione o menor de idade como dependente para acessar exames e semelhantes.',
                )
                return
            }

            if (codeError === 'UNAUTHORIZED') {
                setFailedPasswordCount(failedPasswordCount + 1)
                setFieldError('cpf', ' ')
                setFieldError('password', 'Ops... tente novamente ou vá em "Esqueci a senha"')
                return
            }

            if (codeError === 'INTERNAL_SERVER_ERROR' || codeError === 'UNKNOWN') {
                setFieldError('cpf', 'Ops! Problemas técnicos em nossos servidores. Tente novamente mais tarde.')
                setFieldError('password', ' ')
                return
            }

            setFieldError('cpf', 'Ops! Problemas técnicos em nossos servidores. Tente novamente mais tarde.')
            setFieldError('password', ' ')
            const step = 'login_failure_cpf'
            dispatchAnalytics(step)
        } finally {
            setSubmitting(false)
        }
    }

    const handleLinkAuthUser = async ({
        cpf,
        id,
        setFieldError,
        setSubmitting,
    }: {
        cpf: string
        id: string
        setFieldError: (field: string, value: string | undefined) => void
        setTouched: (value: {}) => void
        setFieldValue: (field: string, value: boolean) => void
        setSubmitting: (value: boolean) => void
    }) => {
        try {
            const payload = {
                cpf: clearCpf(cpf),
                id,
            }

            await authenticateWithLink(payload)

            redirectToPath({
                defaultRedirect: ROUTES_PATH.HOME,
            })
        } catch (error) {
            const codeError = error?.graphQLErrors?.[0].extensions?.code

            if (codeError === 'NOTEXISTS' || codeError === 'DECEASED') {
                dispatchAnalyticsEventFeedBack('confirmCpf', { errorMessage: 'NOTEXISTS' })
                setFieldError('cpf', 'CPF inválido, tente novamente.')
                return
            }

            if (codeError === 'INVALIDFEDERALTAXID') {
                dispatchAnalyticsEventFeedBack('confirmCpf', { errorMessage: 'NOTEXISTS' })
                setFieldError('cpf', 'Parece que o número informado contém erros, revise e tente novamente.')
                return
            }
            if (
                codeError === 'MAGICLINKMAXATTEMPTSREACHED' ||
                codeError === 'MAGICLINKALREADYUSED' ||
                codeError === 'MAGICLINKEXPIRED'
            ) {
                history.push(ROUTES_PATH.SIGN_IN)
                return
            }

            if (codeError === 'INTERNAL_SERVER_ERROR' || codeError === 'UNKNOWN') {
                setFieldError('cpf', 'Ops! Problemas técnicos em nossos servidores. Tente novamente mais tarde.')
                setFieldError('password', ' ')
                return
            }

            setFieldError('cpf', 'Ops! Problemas técnicos em nossos servidores. Tente novamente mais tarde.')
            setFieldError('password', ' ')
        } finally {
            setSubmitting(false)
        }
    }

    async function handleAuthUserAfterChangePassword({ cpf, password }: handleAuthUserAfterChangePasswordProps): Promise<void> {
        const recaptchaToken = await executeRecaptcha('login')
        const payload = {
            username: convertToNumber(cpf),
            password,
            recaptchaToken: recaptchaToken,
        }

        await authenticate(payload)
    }

    return {
        handleAuthUser,
        handleLinkAuthUser,
        handleAuthUserAfterChangePassword,
        setIsLogin,
        hasValidatedContact,
        failedPasswordCount,
        isLogin,
        logout,
    }
}
